import { observer } from "mobx-react";
import { useEffect } from "react";
import { CarSubmission, ILpPrediction } from "@carfax/web-ui/components/CarSubmission";
import { useRouter } from "next/router";
import { useStores } from "hooks";
import { useRoutes } from "domain/Routing/hooks/useRoutes";
import { RecordCheckErrorMessages } from "utils/pricing-response/RecordCheckErrorMessages";
import { useTranslations } from "next-intl";
import { linkUtil } from "utils/i18n/link-util";
import { colors } from "@carfax/web-ui/core";
import ContentLoading from "components/Layout/ContentLoading";
import SampleReport from "components/SampleReport";
import VinSearchComponent from "./components/VinSearchComponent";
import { Image } from "@carfax/contentful/types/content-type";
import { zoomDataLayerEvents } from "./utils/zoomDataLayerEvents";
import { BackendApi } from "utils/backend-api/backend-api";
import { ApiConfig } from "utils/backend-api/api-config";
import { ApiRoutes } from "utils/backend-api/api-routes";
import { savePartnerUrl } from "utils/partnerUrlSessionStorage";
import { capitalizeVehicleText, normalizeModel } from "./utils/formatVehicleText";
import { replaceMakeModelPlaceholders } from "./utils/replaceMakeModelPlaceholders";
import getMakeModelButtonText from "./utils/makeModelButtonText";
import { useNoRecordsReportAvailable } from "hooks/useNoRecordsReportAvailable";
import { usePartnerRecordCheck } from "./hooks/usePartnerRecordCheck";
import { useTextLinkParams } from "utils/text-link/TextLinkParamsContext";
import { useFeatureFlags } from "contexts/FeatureFlagsContext";
import { getIntentQuestionsToggleForLang } from "domain/FeatureFlags/getIntentQuestionsToggleForLang";
import { StickyBar } from "@carfax/web-ui/components/StickyBar";
import { useInView } from "react-intersection-observer";

export interface PartnerHeaderProps {
  module: {
    title: string;
    partnerLogo: Image;
    subtitle: string;
    subtitleWithoutVin: string;
    titleWithoutVin: string;
    afterVinCheck: string;
    isZoomEnabled?: boolean;
    isLicensePlateDetection?: boolean;
  };
}

const PartnerHeader = observer(({ module }: PartnerHeaderProps) => {
  "use no memo";
  const t = useTranslations("pages-record-check");
  const tCommon = useTranslations("common");
  const { recordCheckStore } = useStores();
  const routes = useRoutes();
  const router = useRouter();
  const showReportAvailable = useNoRecordsReportAvailable(recordCheckStore.reportDataTotal);

  const textLinkParams = useTextLinkParams();
  const { pic: picture, adurl } = {
    pic: (router.query.pic as string | undefined) ?? textLinkParams?.pic,
    adurl: router.query.adurl as string | undefined,
  };

  const { isLoading, isVinValid, showContent, makeAndModel, encryptedVin, totalRecords, vhrAlreadyBought } =
    usePartnerRecordCheck();

  const { flags } = useFeatureFlags();
  const isIntentQuestionsBeforePreviewEnabled = getIntentQuestionsToggleForLang(flags);
  let ctaRoute = routes.previewPage;
  if (isIntentQuestionsBeforePreviewEnabled) {
    ctaRoute = routes.intentQuestions;
  }

  const { ref: ctaRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (adurl) {
      savePartnerUrl(adurl);
    }
  }, []);

  const resolvedMake = (isVinValid ? capitalizeVehicleText(recordCheckStore.make) : makeAndModel.make) || "";
  const resolvedModel =
    (isVinValid ? normalizeModel(recordCheckStore.make, recordCheckStore.model) : makeAndModel.model) || "";
  const resolvedTitle = replaceMakeModelPlaceholders(module.title, { make: resolvedMake, model: resolvedModel });
  const resolvedTitleWithoutVin = replaceMakeModelPlaceholders(module.titleWithoutVin, {
    make: resolvedMake,
    model: resolvedModel,
  });
  const resolvedSubtitle = replaceMakeModelPlaceholders(
    module.subtitle,
    { make: resolvedMake, model: resolvedModel },
    true,
  );
  const resolvedSubtitleWithoutVin = replaceMakeModelPlaceholders(
    module.subtitleWithoutVin,
    { make: resolvedMake, model: resolvedModel },
    true,
  );
  const resolvedCtaText =
    getMakeModelButtonText(
      t.raw("partner.partnerCarSubmission.ctaMakeModelText") as string,
      resolvedMake,
      resolvedModel,
    ) || t("partner.partnerCarSubmission.linkToPreview");

  const onCtaClick = (e) => {
    e.preventDefault();
    router.push(linkUtil.i18nLink(ctaRoute));
  };

  const carSubmissionPropsCommon = {
    partnerLogo: module.partnerLogo?.url,
    carImage: picture,
    isZoomEnabled: module.isZoomEnabled,
    isLicensePlateDetection: module.isLicensePlateDetection,
    zoomDataLayerEvents: zoomDataLayerEvents,
    fetchLpPrediction: (carImageUrl: string) =>
      new BackendApi(ApiConfig.buildBackendUrlForClient()).request<ILpPrediction>(
        ApiRoutes.getLpPredictionForCarImage(carImageUrl),
      ),
    afterVinCheck: module.afterVinCheck,
    vhrFound: showReportAvailable
      ? t("preview.makeAndModel.headlineNoRecords")
      : t("preview.makeAndModel.headline", { count: totalRecords }),
    isVinValid,
    onCtaClick: onCtaClick,
    ctaLink: linkUtil.i18nLink(ctaRoute),
    ctaText: resolvedCtaText,
    VinSearchComponent: vhrAlreadyBought ? (
      <VinSearchComponent errorMsgOnLoad={RecordCheckErrorMessages.REPORT_ALREADY_BOUGHT} vinOnLoad={encryptedVin} />
    ) : (
      <VinSearchComponent />
    ),
    showContent,
    translations: {
      partnerCarImageAlt: tCommon("accessibility.partnerCarImageAlt"),
      ariaLabelZoomInButton: tCommon("accessibility.ariaLabelZoomInButton"),
      ariaLabelZoomOutButton: tCommon("accessibility.ariaLabelZoomOutButton"),
      imageHint: t("partner.partnerCarSubmission.imageHint"),
      useTwoFingersHint: t("partner.partnerCarSubmission.useTwoFingersHint"),
      partnerBadgeText: t("partner.partnerCarSubmission.partnerBadgeText"),
      partnerBadgeImageAlt: tCommon("accessibility.badgeImageAlt"),
    },
    title: isVinValid ? resolvedTitle : resolvedTitleWithoutVin,
    subtitle: isVinValid ? resolvedSubtitle : resolvedSubtitleWithoutVin,
    ctaRef: ctaRef,
    secondaryCta: <SampleReport inline buttonText={t("partner.partnerCarSubmission.sampleReportText")} />,
  };

  return (
    <>
      <CarSubmission {...carSubmissionPropsCommon}>
        {isLoading && <ContentLoading color={colors.black.value} />}
      </CarSubmission>
      {isVinValid && (
        <StickyBar
          visible={!inView}
          linkButtonHref={linkUtil.i18nLink(ctaRoute)}
          onButtonClick={onCtaClick}
          buttonTestSelector="sticky-bar-btn"
          buttonText={resolvedCtaText}
        />
      )}
    </>
  );
});

export default PartnerHeader;
